import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../services/supabase';
import { getDonationMessages, getTalentOrders } from './creatorApi';
import type { BadgeKey } from '../nav';

/**
 * Nav badge counts.
 *
 * Lives in one place because the current dashboard computes these three times —
 * once per copy of the nav — and the copies have already drifted.
 *
 * Semantics match the existing dashboard:
 *  - orders: anything not yet delivered/completed, i.e. still the creator's job
 *  - inbox:  unread donation messages PLUS unanswered AMA questions PLUS
 *            ShoutOut conversations with something unread (the Creator Inbox),
 *            because the redesign merges all of them into one destination
 *  - chat:   left at 0 here; unread mentions come from the chat client's own
 *            subscription and would need its realtime channel to be accurate
 *
 * STAYING TRUTHFUL AFTER A WRITE. This hook is mounted by NewWorkspace ABOVE
 * <Routes>, so it never unmounts while the workspace is open — a fetch that only
 * ran on [talentId] would hold its first answer for the whole session. The live
 * dashboard has no such problem: its counts are derived from the same `useState`
 * arrays the views mutate, so clearing a message re-renders the nav. Here the
 * nav and the screens are separate data owners, so the screens announce their
 * writes on `badges:refresh` (see `refreshBadges`) and this re-reads. Consumers
 * that would rather ask directly get `refetch` on the returned object.
 */

const DONE_STATUSES = new Set(['delivered', 'completed', 'cancelled', 'refunded', 'denied']);

/** Fired by any screen whose write changes a badge. */
export const BADGES_REFRESH_EVENT = 'badges:refresh';

/**
 * Tell every mounted nav to re-read its counts.
 *
 * The counts come from the database, so the call that settles a badge is the one
 * made AFTER the server acknowledges the write; a call next to the optimistic
 * patch just re-reads the old number. Overlapping reads are resolved
 * last-requested-wins below, so an extra call is wasted work, never a wrong
 * badge.
 */
export function refreshBadges(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(BADGES_REFRESH_EVENT));
}

/**
 * The counts, plus the handle to re-read them.
 *
 * Deliberately one flat object rather than `{ counts, refetch }`: the shell
 * takes `Partial<Record<BadgeKey, number>>` and only ever indexes it by badge
 * key, so this stays a drop-in for it. `refetch` is not a BadgeKey and is never
 * enumerated as one.
 */
export type BadgeCounts = Partial<Record<BadgeKey, number>> & {
  /** Re-read all counts now. Same work as a `badges:refresh` event. */
  refetch: () => void;
  /** ShoutOut conversations with anything unread (already included in `inbox`). */
  shoutoutUnread: number;
};

/** PostgREST surfaces a missing column grant as SQLSTATE 42501. */
function isPermissionDenied(error: any): boolean {
  return error?.code === '42501' || /permission denied/i.test(String(error?.message ?? ''));
}

/**
 * Whether `archived_at` may be mentioned by this client, cached for the page
 * session. `null` = not yet probed. Intentionally a separate flag from the one
 * in useInboxScreen: importing that module here would pull the whole Inbox
 * (toast, share, artwork) into the shell just to read one boolean.
 */
let archiveSupported: boolean | null = null;

/**
 * Unanswered questions still asking for the creator.
 *
 * `archived_at` exists on the table but was never granted to `authenticated`
 * (see the header of useInboxScreen.ts), and PostgREST rejects a request that so
 * much as FILTERS on an ungranted column. That 403 does not throw — it comes
 * back as `{ count: null, error }` — so filtering unconditionally silently
 * reported ZERO unanswered questions on production, i.e. the inbox badge never
 * lit for AMA at all. Probe once, then fall back to the un-filtered count, which
 * is exactly what the live dashboard counts (`unansweredAmaCount`, :469) and is
 * equivalent anyway while nothing can be archived.
 */
async function countUnansweredAma(talentId: string): Promise<number> {
  try {
    if (archiveSupported !== false) {
      const { count, error } = await supabase
        .from('ask_me_anything_questions')
        .select('id', { count: 'exact', head: true })
        .eq('talent_id', talentId)
        .is('answered_at', null)
        // An archived question is handled as far as the creator is concerned, so
        // it must not keep the nav badge lit.
        .is('archived_at', null);

      if (!error) {
        archiveSupported = true;
        return count ?? 0;
      }
      if (!isPermissionDenied(error)) return 0;
      archiveSupported = false;
    }

    const { count } = await supabase
      .from('ask_me_anything_questions')
      .select('id', { count: 'exact', head: true })
      .eq('talent_id', talentId)
      .is('answered_at', null);
    return count ?? 0;
  } catch {
    return 0;
  }
}

/**
 * ShoutOut conversations with anything unread. Zero while the Creator Inbox is
 * switched off for this creator (the database answers 0), and zero when the
 * session is not this page's owner — staff opening an unclaimed page with
 * their own session would otherwise count STAFF's own threads here.
 */
async function countShoutOutUnread(ownerUserId: string): Promise<number> {
  try {
    const { data: auth } = await supabase.auth.getSession();
    if (auth.session?.user?.id !== ownerUserId) return 0;
    const { data, error } = await supabase.rpc('creator_inbox_unread_count');
    return error ? 0 : Number(data) || 0;
  } catch {
    return 0;
  }
}

/** Staff can write at any moment, so this count is polled rather than event-only. */
const SHOUTOUT_POLL_MS = 60_000;

export function useBadgeCounts(
  talentId: string | null | undefined,
  ownerUserId?: string | null
): BadgeCounts {
  const [counts, setCounts] = useState<Partial<Record<BadgeKey, number>>>({});
  const [shoutoutUnread, setShoutoutUnread] = useState(0);

  // Last-write-wins across overlapping reads: a refresh fired while an earlier
  // one is still in flight must not be overwritten by that older answer.
  const ticketRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refetch = useCallback(async () => {
    if (!talentId) return;
    const ticket = (ticketRef.current += 1);

    const [orders, donations, ama] = await Promise.all([
      getTalentOrders(talentId).catch(() => []),
      getDonationMessages(talentId).catch(() => []),
      countUnansweredAma(talentId),
    ]);

    if (!mountedRef.current || ticket !== ticketRef.current) return;

    const pendingOrders = orders.filter(
      (o) => !DONE_STATUSES.has(String(o.status || '').toLowerCase())
    ).length;
    const unreadDonations = donations.filter((m) => !m.is_read).length;

    setCounts({
      orders: pendingOrders,
      inbox: unreadDonations + ama,
    });
  }, [talentId]);

  useEffect(() => {
    if (!talentId) return undefined;

    void refetch();

    // The event, not a subscription: the writes that change these numbers are
    // this creator's own taps in this same tab, and a realtime channel per badge
    // would be three more sockets for something a screen already knows.
    const onRefresh = () => {
      void refetch();
    };
    window.addEventListener(BADGES_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(BADGES_REFRESH_EVENT, onRefresh);
  }, [talentId, refetch]);

  useEffect(() => {
    if (!ownerUserId) {
      setShoutoutUnread(0);
      return undefined;
    }
    let cancelled = false;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      void countShoutOutUnread(ownerUserId).then((n) => {
        if (!cancelled) setShoutoutUnread(n);
      });
    };
    refresh();
    const timer = window.setInterval(refresh, SHOUTOUT_POLL_MS);
    window.addEventListener('focus', refresh);
    window.addEventListener(BADGES_REFRESH_EVENT, refresh);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      window.removeEventListener(BADGES_REFRESH_EVENT, refresh);
    };
  }, [ownerUserId]);

  // Stable while the numbers are: the shell passes this straight down to four
  // navs, and a fresh object on every render would re-render all of them.
  return useMemo(
    () => ({
      ...counts,
      ...(counts.inbox !== undefined || shoutoutUnread > 0
        ? { inbox: (counts.inbox ?? 0) + shoutoutUnread }
        : {}),
      refetch,
      shoutoutUnread,
    }),
    [counts, shoutoutUnread, refetch]
  );
}
